import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useResetPassword } from 'react-easyrocket';
import { FieldType, useForm, validateMaxLength, validateRequired } from 'src/hooks/useForm';
import { openChat } from 'src/utils';
import { Button } from 'src/components/Buttons/Button';
import { Input } from 'src/components/Inputs/Input';
import { ModalWrapper } from 'src/components/ModalWrapper';
import { useModalContext } from 'src/hooks/useModalContext';
import { useAuthFlow } from 'src/modules/authFlow/hooks/useAuthFlow';

const ResetPasswordModal = () => {
  const [showSuccessInfo, setShowSuccessInfo] = useState(false);
  const { mutateAsync: resetPassword } = useResetPassword();
  const { closeModal } = useModalContext();
  const { startRegistration } = useAuthFlow();
  const { t } = useTranslation();
  const {
    values,
    errors,
    fieldsProps: { emailProps },
    touched,
    isFormValid
  } = useForm<{ email: FieldType.Text }>({
    email: {
      placeholder: 'Email',
      fieldType: FieldType.Text,
      validation: [validateRequired, validateMaxLength(256)]
    }
  });

  const handleCreateAccountClick = () => {
    startRegistration({ replace: true });
  };

  const onResetPassword = async () => {
    const response = await resetPassword({ email: values.email });
    setShowSuccessInfo(response.data.success);
  };

  return (
    <ModalWrapper>
      <div className="flex flex-col items-center gap-[15px] p-[15px] py-[20px] w-[300px] r_xsm:w-full bg-lm_bg">
        <p className="text-[24px] font-bold tracking-[0.03em] text-lm_title text-center w-full">
          {t('modalResetPassword.title')}
        </p>
        {!showSuccessInfo ? (
          <>
            <p className="font-medium text-[12px] text-center w-[80%] text-lm_text">
              {t('modalResetPassword.subtitle')}
            </p>
            <Input {...emailProps} />
            {touched.email && errors.email && (
              <span className="text-i_text_error text-xs">{errors.email}</span>
            )}
            <div className=" flex justify-center items-center w-full">
              <Button
                disabled={!isFormValid}
                mode="button-primary"
                onClick={onResetPassword}
                className="container-with-wide-content max-w-full"
                title={`${t('modalResetPassword.button')}`}>
                {t('modalResetPassword.button')}
              </Button>
            </div>
            <div className="flex flex-row">
              <p className="text-center font-medium text-[16px]  text-lm_text">
                {t('modalResetPassword.notRegist')}
                <span
                  className="pl-[3px] font-thin text-lm_create_account text-[16px]  underline cursor-pointer"
                  onClick={handleCreateAccountClick}>
                  {t('modalResetPassword.createNewAcc')}
                </span>
              </p>
            </div>
          </>
        ) : (
          <>
            <p className="font-medium text-[12px] text-lm_title text-center w-[80%]">
              {t('modalResetPassword.thankYouText')}
            </p>
            <p className="font-medium text-[12px] text-lm_text text-center w-[80%]">
              {t('modalResetPassword.spamText')}
            </p>
            <p className="font-medium text-[12px] text-lm_text text-center w-[80%]">
              {t('modalResetPassword.notReceivedLetter')}{' '}
              <span
                className="text-lm_create_account cursor-pointer underline"
                onClick={() => {
                  closeModal();
                  openChat();
                }}>
                {t('modalResetPassword.support')}
              </span>
            </p>
            <Button mode="button-primary" onClick={closeModal} title="okay">
              {t('modalResetPassword.okay')}
            </Button>
          </>
        )}
      </div>
    </ModalWrapper>
  );
};

export default ResetPasswordModal;
